import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, ArrowUpRight, Lock, Network } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useFrameworkHierarchy, REQUIREMENT_STATUS_LABEL, partialDetail,
  ownerSummary, evidenceSummary,
  type FrameworkHierarchy, type HierarchyRequirement, type FrameworkNode,
} from '@/api/hooks/useFrameworkHierarchy';

/**
 * FRAMEWORK STRUCTURE — the framework's own tree, with each requirement's status.
 *
 * ⚠ A DECOMPOSITION, NOT A SECOND COMPUTATION. Every status here is the value the API emits once
 * from the classified CTE and the framework card rolls up. Nothing on this page derives a status,
 * because two computations of one truth drift — this codebase has the scar.
 *
 * ⚠ GENERIC IN THE FRAMEWORK'S OWN SHAPE. Nodes nest to any depth and carry their own level name;
 * nothing here knows what a "function" is. A flat or two-level standard renders its own structure.
 *
 * ⚠ READ-ONLY. A requirement is not a work unit — the work happens on controls, and offering an
 * action here would invite someone to "do" a requirement.
 */

const GRID = 'grid grid-cols-[minmax(0,1fr)_220px_170px_150px_170px] gap-4';

const STATUS_TONE: Record<string, string> = {
  met: 'bg-cert-valid-fg/15 text-cert-valid-fg',
  partially_met: 'bg-cert-warn-fg/15 text-cert-warn-fg',
  not_met: 'bg-cert-expired-fg/15 text-cert-expired-fg',
  not_assessed: 'bg-white/[0.06] text-text-muted',
};

interface Group { node: FrameworkNode; children: Group[]; reqs: HierarchyRequirement[] }

/** Build the framework's own tree. ⚠ Depth is whatever the data says; nothing is assumed. */
function buildTree(h: FrameworkHierarchy): { roots: Group[]; orphans: HierarchyRequirement[] } {
  const byCode = new Map<string, Group>();
  h.nodes.forEach((n) => byCode.set(n.node_code, { node: n, children: [], reqs: [] }));
  const roots: Group[] = [];
  h.nodes.forEach((n) => {
    const g = byCode.get(n.node_code)!;
    const parent = n.parent_code ? byCode.get(n.parent_code) : undefined;
    if (parent) parent.children.push(g); else roots.push(g);
  });
  // ⚠ A requirement with no parent is NORMAL, not incomplete — a flat framework is legitimate,
  //    and dropping such rows would hide real content.
  const orphans: HierarchyRequirement[] = [];
  h.requirements.forEach((r) => {
    const g = r.parent_code ? byCode.get(r.parent_code) : undefined;
    if (g) g.reqs.push(r); else orphans.push(r);
  });
  return { roots, orphans };
}

/** Counts for a subtree — read from the rows, never recomputed from anything else. */
function tally(g: Group): { total: number; met: number; mapped: number; unmapped: number } {
  const here = g.reqs;
  const sub = g.children.map(tally);
  return {
    total: here.length + sub.reduce((a, s) => a + s.total, 0),
    met: here.filter((r) => r.requirement_status === 'met').length
      + sub.reduce((a, s) => a + s.met, 0),
    mapped: here.filter((r) => r.controls.length > 0).length
      + sub.reduce((a, s) => a + s.mapped, 0),
    unmapped: here.filter((r) => r.status_axis === 'coverage').length
      + sub.reduce((a, s) => a + s.unmapped, 0),
  };
}

export default function FrameworkHierarchyPage() {
  const { reference } = useParams();
  const { data, isLoading, isError } = useFrameworkHierarchy(reference);
  const [shut, setShut] = useState<Record<string, boolean>>({});

  const tree = useMemo(() => (data ? buildTree(data) : null), [data]);

  const totals = useMemo(() => {
    const r = data?.requirements ?? [];
    const n = (s: string) => r.filter((x) => x.requirement_status === s).length;
    const unmapped = r.filter((x) => x.status_axis === 'coverage').length;
    const excluded = r.filter((x) => x.status_axis === 'excluded').length;
    // ⚠ THE HONEST DENOMINATOR. Unmapped and excluded requirements are not failures — nobody has
    //    failed them, and counting them would blame the tenant for content nobody has mapped yet.
    const scorable = r.length - unmapped - excluded;
    return {
      total: r.length, unmapped, excluded, scorable,
      met: n('met'), partially_met: n('partially_met'),
      not_met: n('not_met'), not_assessed: n('not_assessed'),
      // ⚠ NULL, never 0%, when there is nothing to score — 0% states the opposite of "unmeasured".
      pct: scorable > 0 ? Math.round((n('met') / scorable) * 100) : null,
    };
  }, [data]);

  if (isLoading) return <HierarchySkeleton />;
  if (isError || !data || !tree) {
    return (
      <div className="p-8 text-[13px] text-text-secondary" data-testid="hierarchy-error">
        This framework could not be loaded, or is not available to your organization.
      </div>
    );
  }

  return (
    <div className="p-8 pb-20" data-testid="framework-hierarchy-page">
      <div className="mb-1 font-mono text-[11px] uppercase tracking-[0.14em] text-text-faint">
        app.verigrc.com / compliance / frameworks / {data.display_code}
      </div>
      <Link to="/compliance/workspace/frameworks"
            className="mb-3 inline-flex items-center gap-1.5 text-[12px] text-text-secondary hover:text-text-primary">
        <ChevronRight className="h-3.5 w-3.5 rotate-180" aria-hidden="true" /> Frameworks
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-[26px] font-semibold tracking-tight text-text-primary">{data.name}</h1>
            {/* ⚠ READ ONLY, said out loud. The work happens on controls; a badge here stops
                someone hunting for an action this page will never offer. */}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-2.5 py-0.5 text-[11px] text-text-muted">
              <Lock className="h-3 w-3" aria-hidden="true" /> Read only
            </span>
          </div>
          <p className="mt-2 max-w-prose text-[13px] text-text-secondary">
            Explore this framework&apos;s native structure, its mapped VeriSAQ controls, and each
            requirement&apos;s status.
          </p>
        </div>
        {/* ⚠ JOINS "Open Controls" rather than replacing it — two destinations, two questions. */}
        <Link to="/compliance/workspace/controls"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border-subtle px-3 py-1.5 text-[12.5px] text-text-secondary hover:bg-white/[0.04]">
          Open Controls <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <Card label="Framework progress — requirements met">
          <div className="flex items-center gap-5">
            <div className="text-[30px] font-semibold tabular-nums text-text-primary">
              {totals.pct === null ? 'Not assessed' : `${totals.pct}%`}
            </div>
            <div className="text-[11.5px] text-text-secondary">
              {/* ⚠ VeriGRC-DERIVED, said explicitly — this is not a score the standard defines,
                  and presenting it as one would misrepresent NIST. */}
              Requirements met (VeriGRC-derived)
            </div>
          </div>
          <div className="mt-4 flex gap-6 border-t border-border-subtle pt-3 text-[12px]">
            <span className="text-text-primary">
              <strong className="tabular-nums">{totals.scorable}</strong>
              <span className="text-text-muted">/{totals.total}</span> requirements scored
            </span>
          </div>
        </Card>

        <Card label="Requirement status" hint="derived from mapped control posture">
          <div className="text-[17px] font-semibold text-text-primary">
            {totals.met} met · {totals.partially_met} partially met
          </div>
          <div className="mt-3 space-y-1.5">
            {(['met', 'partially_met', 'not_met', 'not_assessed'] as const).map((k) => (
              <div key={k} className="flex items-center gap-2 text-[12.5px]">
                <span className={cn('h-2 w-2 rounded-full',
                  k === 'met' ? 'bg-cert-valid-fg'
                    : k === 'partially_met' ? 'bg-cert-warn-fg'
                      : k === 'not_met' ? 'bg-cert-expired-fg' : 'bg-text-faint/50')} />
                <span className="text-text-secondary">{REQUIREMENT_STATUS_LABEL[k]}</span>
                <span className="ml-auto tabular-nums text-text-primary">{totals[k]}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* ⚠ COVERAGE IS ITS OWN CARD, and that separation is the point. "No control maps here" is
            not a failing requirement — folding it into Not Met would blame the tenant for content
            nobody has mapped, and folding it into the status axis would let coverage move a
            status headline. */}
        <Card label="Coverage & gaps">
          <Row label="Not mapped" value={totals.unmapped}
               hint="no mapped control yet — separate from Not Met" />
          <Row label="Not applicable" value={totals.excluded}
               hint="excluded with a rationale, not a gap" />
        </Card>
      </div>

      <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-faint">
        Structure
      </div>
      <div className="space-y-3">
        {tree.roots.map((g) => (
          <GroupBlock key={g.node.node_code} g={g} depth={0} shut={shut} setShut={setShut} />
        ))}
        {tree.orphans.length > 0 && (
          <section className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
            {/* ⚠ A flat framework is legitimate — requirements with no parent are shown, never
                dropped, because dropping them would hide real content. */}
            <div className="border-b border-border-subtle px-4 py-2.5 text-[13px] font-semibold text-text-primary">
              Requirements
            </div>
            <ReqTable reqs={tree.orphans} />
          </section>
        )}
        {tree.roots.length === 0 && tree.orphans.length === 0 && (
          <div data-testid="hierarchy-empty"
               className="rounded-xl border border-dashed border-border-subtle bg-bg-surface p-10 text-center">
            <Network className="mx-auto h-7 w-7 text-text-faint" aria-hidden="true" />
            <p className="mt-3 text-[15px] font-semibold text-text-primary">
              This framework has no published requirements yet
            </p>
            <p className="mt-1.5 text-[13px] text-text-secondary">
              Its structure will appear here once its content is published.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function GroupBlock({
  g, depth, shut, setShut,
}: {
  g: Group; depth: number; shut: Record<string, boolean>;
  setShut: (v: Record<string, boolean>) => void;
}) {
  const closed = shut[g.node.node_code];
  const t = tally(g);
  return (
    <section className={cn('overflow-hidden rounded-xl border border-border-subtle bg-bg-surface',
      depth > 0 && 'rounded-none border-x-0 border-b-0')}>
      <button
        type="button"
        onClick={() => setShut({ ...shut, [g.node.node_code]: !closed })}
        className={cn('flex w-full items-center gap-3 px-4 text-left',
          depth === 0
            ? 'border-b border-border-subtle bg-accent-cyan/[0.06] py-3'
            : 'border-t border-border-subtle bg-white/[0.022] py-2')}
      >
        {closed ? <ChevronRight className="h-4 w-4 text-text-muted" aria-hidden="true" />
          : <ChevronDown className="h-4 w-4 text-text-muted" aria-hidden="true" />}
        <span className={cn('rounded px-2 py-0.5 font-mono text-[11px]',
          depth === 0 ? 'bg-accent-cyan/15 text-accent-cyan' : 'text-text-muted')}>
          {g.node.node_code}
        </span>
        <span className={cn('font-semibold text-text-primary',
          depth === 0 ? 'text-[15px]' : 'text-[13.5px]')}>{g.node.name}</span>
        {/* ⚠ The framework's OWN level name, shown rather than assumed. */}
        <span className="font-mono text-[10px] uppercase tracking-wider text-text-faint">
          {g.node.node_type}
        </span>
        <span className="ml-auto text-[11.5px] text-text-muted">
          {t.mapped} of {t.total} mapped · {t.met} met
          {t.unmapped > 0 && ` · ${t.unmapped} not mapped`}
        </span>
      </button>

      {!closed && (
        <>
          {g.reqs.length > 0 && <ReqTable reqs={g.reqs} />}
          {g.children.map((c) => (
            <GroupBlock key={c.node.node_code} g={c} depth={depth + 1} shut={shut} setShut={setShut} />
          ))}
        </>
      )}
    </section>
  );
}

function ReqTable({ reqs }: { reqs: HierarchyRequirement[] }) {
  // ⚠ Expanding reconciles the aggregate against its parts: the same source, one grain down.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (k: string) => setExpanded((e) => ({ ...e, [k]: !e[k] }));
  return (
    <div>
      <div className={cn(GRID, 'border-b border-border-subtle bg-white/[0.015] px-4 py-1.5 font-mono text-[10px] uppercase tracking-wider text-text-faint')}>
        <span>Requirement</span><span>Mapped controls</span><span>Responsible</span>
        <span>Requirement status</span><span>Evidence</span>
      </div>
      {reqs.map((r) => {
        const detail = r.requirement_status === 'partially_met' ? partialDetail(r.bucket) : null;
        const open = expanded[r.requirement_reference];
        return (
          <div key={r.requirement_reference}
               className={cn(GRID, 'items-start border-t border-border-subtle px-4 py-2.5 hover:bg-white/[0.02]')}>
            <div className="min-w-0">
              <span className="font-mono text-[12px] text-accent-cyan">{r.display_code}</span>
              <span className="ml-2.5 text-[13.5px] text-text-primary">{r.title}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.controls.length === 0
                ? <span className="text-[12.5px] text-text-muted">Not mapped</span>
                : r.controls.map((c) => (
                  // ⚠ Each chip is its OWN control — the requirement surfaces them through the
                  //    mapping, it does not own them. Owner and evidence attach HERE per control
                  //    when they land, never as one value claimed for the requirement.
                  <Link key={c.control_reference}
                        to={`/compliance/workspace/controls/${c.control_reference}`}
                        title={`${c.title} — ${c.relationship_type}`}
                        className="inline-flex items-center gap-1 rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[11.5px] text-accent-cyan hover:bg-white/[0.1]">
                    {c.display_code}<ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                  </Link>
                ))}
              {open && (
                // ⚠ PER CONTROL, unambiguous: each line names WHICH control the owner and the
                //    evidence belong to, so the aggregate above can be checked against its parts.
                <ul className="mt-1.5 w-full space-y-1">
                  {r.controls.map((c) => (
                    <li key={c.control_reference} className="text-[11px] text-text-muted">
                      <span className="font-mono text-accent-cyan">{c.display_code}</span>
                      {' — '}{c.owner_name ?? 'Unassigned'}
                      {' · '}{c.evidence_count} evidence
                      {' · '}{c.relationship_type}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* ⚠ AN AGGREGATE OF THE MAPPED CONTROLS' OWNERS, shaped by cardinality — never a
                single name presented as THE requirement's owner. A requirement has no owner
                field, so the fabrication is structurally impossible rather than merely avoided. */}
            <div className="text-[12.5px] text-text-secondary">
              {ownerSummary(r.controls)}
              {r.controls.length > 1 && (
                <button type="button" onClick={() => toggle(r.requirement_reference)}
                  className="mt-0.5 block text-[10.5px] text-accent-cyan hover:underline">
                  {open ? 'Hide per control' : 'Show per control'}
                </button>
              )}
            </div>
            <div>
              {r.requirement_status ? (
                <>
                  <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[11.5px]',
                    STATUS_TONE[r.requirement_status])}>
                    {REQUIREMENT_STATUS_LABEL[r.requirement_status]}
                  </span>
                  {detail && (
                    <div className="mt-1 text-[10.5px] text-text-faint">{detail}</div>
                  )}
                </>
              ) : (
                // ⚠ OFF THE STATUS AXIS. An unmapped requirement has no status to report, and
                //    showing "Not Assessed" here would put a coverage gap on the status axis.
                <span className="text-[11.5px] text-text-muted">
                  {r.status_axis === 'coverage' ? 'Not mapped'
                    : r.status_axis === 'excluded' ? 'Not applicable' : 'Out of scope'}
                </span>
              )}
            </div>
            {/* ⚠ THE ROLLUP NAMES ITS SOURCE. A bare count here would read as evidence FOR the
                requirement, which does not exist — evidence is produced against control work. */}
            <div className="text-[12.5px] text-text-secondary">{evidenceSummary(r.controls)}</div>
          </div>
        );
      })}
    </div>
  );
}

function Card({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border-subtle bg-bg-surface p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-text-faint">{label}</span>
        {hint && <span className="text-[10.5px] text-text-faint">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Row({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="border-t border-border-subtle py-2 first:border-t-0 first:pt-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] text-text-primary">{label}</span>
        <span className="tabular-nums text-[15px] text-text-primary">{value}</span>
      </div>
      <div className="mt-0.5 text-[10.5px] text-text-faint">{hint}</div>
    </div>
  );
}

function HierarchySkeleton() {
  return (
    <div className="p-8" data-testid="hierarchy-skeleton">
      <Skeleton className="h-7 w-72" />
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border border-border-subtle bg-bg-surface p-4">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="mt-4 h-8 w-24" />
            <Skeleton className="mt-3 h-3 w-full" />
          </div>
        ))}
      </div>
      <div className="mt-6 space-y-3">
        {[0, 1].map((i) => (
          <div key={i} className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
            <div className="border-b border-border-subtle px-4 py-3"><Skeleton className="h-4 w-56" /></div>
            {[0, 1, 2].map((r) => (
              <div key={r} className="border-t border-border-subtle px-4 py-3">
                <Skeleton className="h-3.5 w-2/3" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
